<script>
  export let tour;
  export let linkedScenes = [];
  export let onClose = () => {};

  import UAParser from "ua-parser-js";
  import CloseButton from "./CloseButton.svelte";
  import TutorialOverlay from "./TutorialOverlay.svelte";
  import Icon from "@iconify/svelte";
  import { fade, fly } from "svelte/transition";
  import { focusTrap } from "./utils.js";
  import { createPopup360XMLUrl } from "./xml.js";
  import { onDestroy, onMount } from "svelte";

  const parser = new UAParser();
  const deviceType = parser.getResult().device["type"];
  const isMobile = deviceType == "mobile";
  const isMobileOrTablet = deviceType === "mobile" || deviceType === "tablet";
  const panoKey = Math.random().toString(36).slice(2);
  const panoTargetId = `popup-map360-pano-${panoKey}`;
  const krpanoViewerId = `popupMap360Krpano_${panoKey.replace(/[^a-zA-Z0-9_]/g, "_")}`;

  let visible = true;
  let popupKrpano = null;
  let xmlUrl = null;
  let panoLoading = true;
  let currentSceneTitle = linkedScenes[0]?.title ?? "";
  let mountInterval = null;
  let blockContextMenuHandler = null;
  let panoContainerEl = null;
  let showTutorialOverlay = false;

  $: hasMultipleScenes = linkedScenes.length > 1;
  $: ariaLabel = linkedScenes.length === 1
    ? `360 view: ${sceneTitleForDisplay(linkedScenes[0].title)}`
    : "360 views";

  function sceneTitleForDisplay(sceneTitle) {
    return (sceneTitle || "").replace(/\[br\]/g, " ");
  }

  function resolveFilteredScenes() {
    if (!tour?.scenes || !linkedScenes?.length) return [];
    return linkedScenes
      .map((linked) => tour.scenes.find((s) => s.id === linked.id))
      .filter(Boolean);
  }

  function updateSceneTitleFromKrpano() {
    if (!popupKrpano) return;
    try {
      const title = popupKrpano.get("scene[get(xml.scene)].title");
      if (title) currentSceneTitle = title;
    } catch (_) {}
  }

  function attachContextMenuBlocker() {
    panoContainerEl = document.getElementById(panoTargetId);
    if (!panoContainerEl) return;
    blockContextMenuHandler = (event) => event.preventDefault();
    panoContainerEl.addEventListener("contextmenu", blockContextMenuHandler, true);
  }

  function detachContextMenuBlocker() {
    if (panoContainerEl && blockContextMenuHandler) {
      panoContainerEl.removeEventListener("contextmenu", blockContextMenuHandler, true);
    }
    panoContainerEl = null;
    blockContextMenuHandler = null;
  }

  function mountKrpano() {
    const filteredScenes = resolveFilteredScenes();
    if (!filteredScenes.length || typeof embedpano !== "function") return false;

    xmlUrl = createPopup360XMLUrl(filteredScenes, { startIndex: 0 });

    embedpano({
      xml: xmlUrl,
      target: panoTargetId,
      id: krpanoViewerId,
      html5: "auto",
      webgl: "auto",
      passQueryParameters: false,
      focus: false,
      onready: (kr) => {
        popupKrpano = kr;
        panoLoading = false;
        showTutorialOverlay = true;
        kr.events.addListener("onnewpano", updateSceneTitleFromKrpano);
        updateSceneTitleFromKrpano();
        kr.set("control.mouse", "drag");
        kr.set("control.touch", "drag");
        kr.call("updatescreen()");
        attachContextMenuBlocker();
        requestAnimationFrame(() => popupKrpano?.call("updatescreen()"));
      },
      initvars: {
        startIndex: 0,
      },
    });

    return true;
  }

  function destroyKrpano() {
    detachContextMenuBlocker();
    if (popupKrpano && typeof popupKrpano.unload === "function") {
      popupKrpano.unload();
      popupKrpano = null;
    }
    const container = document.getElementById(panoTargetId);
    if (container) container.innerHTML = "";
    if (xmlUrl) {
      URL.revokeObjectURL(xmlUrl);
      xmlUrl = null;
    }
  }

  function goPrevScene() {
    popupKrpano?.call("nextscene_loop(-1)");
  }

  function goNextScene() {
    popupKrpano?.call("nextscene_loop(+1)");
  }

  function dismissTutorialOverlay() {
    showTutorialOverlay = false;
  }

  function closePopup() {
    visible = false;
    onClose();
  }

  onMount(() => {
    if (mountKrpano()) return;
    mountInterval = setInterval(() => {
      if (mountKrpano()) clearInterval(mountInterval);
    }, 250);
  });

  onDestroy(() => {
    if (mountInterval) clearInterval(mountInterval);
    destroyKrpano();
  });
</script>

<svelte:head>
  <script src="tour.js"></script>
</svelte:head>

{#if visible}
  <div class="popup-map360-modal" use:focusTrap aria-label={ariaLabel}>
    <div class="popup-map360-area">
      <div class="popup-map360-bg" on:click={closePopup} transition:fade={{ duration: 500 }}></div>
      <div class="popup-map360-center">
        <div class="popup-map360-window-wrap">
          <div class="popup-map360-window" transition:fly={{ y: 800, duration: 500 }}>
            <div class="popup-map360-layout">
              <div class="popup-map360-content">
                <div class="popup-map360-pano-wrap" class:loading={panoLoading}>
                  <div id={panoTargetId} class="popup-map360-pano"></div>
                  <TutorialOverlay
                    contained
                    visible={showTutorialOverlay && !panoLoading}
                    {isMobileOrTablet}
                    onDismiss={dismissTutorialOverlay}
                  />
                  <div class="popup-map360-scene-nav" class:single-scene={!hasMultipleScenes}>
                    <div class="popup-map360-scene-nav-inner">
                      {#if hasMultipleScenes}
                        <button
                          type="button"
                          class="popup-map360-scene-nav-btn"
                          on:click|stopPropagation={goPrevScene}
                          aria-label="Previous scene"
                        >
                          <Icon icon="material-symbols:chevron-left-rounded" aria-hidden="true" />
                        </button>
                      {/if}
                      <span class="popup-map360-scene-title">{sceneTitleForDisplay(currentSceneTitle)}</span>
                      {#if hasMultipleScenes}
                        <button
                          type="button"
                          class="popup-map360-scene-nav-btn"
                          on:click|stopPropagation={goNextScene}
                          aria-label="Next scene"
                        >
                          <Icon icon="material-symbols:chevron-right-rounded" aria-hidden="true" />
                        </button>
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
              <div class="close-button-container">
                <CloseButton
                  close={closePopup}
                  color="#ffffff"
                  useAbsolutePosition={false}
                  marginBottom={isMobile ? "0" : "1rem"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .popup-map360-modal {
    position: fixed;
    inset: 0;
    z-index: 3000;
  }

  .popup-map360-area {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 3001;
    isolation: isolate;
  }

  .popup-map360-center {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 80vw;
    max-width: min(80vw, calc(100vw - 2rem));
    height: 80vh;
    max-height: min(80vh, calc(100vh - 2rem));
    margin: auto;
    box-sizing: border-box;
    pointer-events: auto;
  }

  .popup-map360-window-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 0;
    box-sizing: border-box;
  }

  .popup-map360-window {
    position: relative;
    z-index: 1;
    display: flex;
    background: transparent;
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    margin: 0;
    overflow: visible;
  }

  .popup-map360-layout {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    max-height: 100%;
    width: 100%;
    min-height: 0;
  }

  .popup-map360-content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: auto;
    max-width: none;
    height: 100%;
    max-height: 100%;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
  }

  .close-button-container {
    flex-shrink: 0;
  }

  .popup-map360-scene-nav {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 1.25rem;
    background: none;
    color: #fff;
    font-family: var(--font-heading), sans-serif;
    pointer-events: none;
  }

  .popup-map360-scene-nav-inner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    max-width: calc(100% - 1rem);
    min-width: 0;
    pointer-events: auto;
  }

  .popup-map360-scene-nav.single-scene .popup-map360-scene-nav-inner {
    justify-content: center;
  }

  .popup-map360-scene-title {
    flex: 0 1 auto;
    min-width: 0;
    text-align: center;
    font-size: 3rem;
    font-weight: 400;
    line-height: 1.25;
    text-shadow:
      0 1px 3px rgba(0, 0, 0, 0.85),
      0 2px 10px rgba(0, 0, 0, 0.65);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .popup-map360-scene-nav-btn {
    appearance: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 3rem;
    height: 3rem;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: var(--theme-color);
    color: #fff;
    font-size: 1rem;
    line-height: 1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      opacity 0.15s ease;
  }

  .popup-map360-scene-nav-btn :global(svg) {
    width: 2em;
    height: 2em;
    flex-shrink: 0;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
  }

  .popup-map360-scene-nav-btn:hover {
    background: var(--theme-color);
    border-color: #fff;
    border-width: 2px;
  }

  .popup-map360-scene-nav-btn:active {
    opacity: 0.9;
  }

  .popup-map360-scene-nav-btn:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.85);
    outline-offset: 2px;
  }

  .popup-map360-pano-wrap {
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    min-height: 0;
    background: #000;
    touch-action: none;
    -webkit-touch-callout: none;
  }

  .popup-map360-pano-wrap.loading {
    background: #000;
  }

  .popup-map360-pano {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: grab;
    -webkit-touch-callout: none;
    user-select: none;
  }

  .popup-map360-pano :global(div),
  .popup-map360-pano :global(canvas) {
    width: 100% !important;
    height: 100% !important;
    touch-action: none;
    pointer-events: auto;
  }

  .popup-map360-bg {
    position: fixed;
    inset: 0;
    z-index: 1;
    background: rgba(0, 0, 0, 0.66);
    backdrop-filter: blur(2px);
  }

  @media only screen and (max-width: 600px) {
    .popup-map360-center {
      width: 80vw;
      max-width: min(80vw, calc(100vw - 1rem));
      height: 80dvh;
      max-height: min(80dvh, calc(100dvh - 1rem));
      margin: auto;
    }

    .popup-map360-window-wrap {
      width: 100%;
      max-width: 100%;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-map360-window {
      width: 100%;
      max-width: 100%;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-map360-layout {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      height: 100%;
      min-height: 0;
      width: 100%;
    }

    .popup-map360-content {
      width: 100%;
      max-width: 100%;
      flex: 1 1 auto;
      height: auto;
      min-height: 0;
      max-height: none;
      border-radius: 6px;
    }

    .popup-map360-pano-wrap {
      width: 100%;
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }

    .popup-map360-scene-nav {
      padding: 0.75rem 0.85rem;
    }

    .popup-map360-scene-nav-inner {
      gap: 0.65rem;
    }

    .popup-map360-scene-title {
      font-size: 1.5rem;
    }

    .popup-map360-scene-nav-btn {
      width: 2rem;
      height: 2rem;
      font-size: 1.25rem;
      border-radius: 8px;
    }

    .popup-map360-scene-nav-btn :global(svg) {
      width: 1em;
      height: 1em;
    }

    .close-button-container {
      flex-shrink: 0;
      align-self: center;
    }
  }
</style>
